import { createSlice } from "@reduxjs/toolkit";

const loadVoiceFlowLocks = () => {
  try {
    const stored = localStorage.getItem("voiceFlowLocks");
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error("Failed to parse voiceFlowLocks from localStorage:", error);
    return {};
  }
};

const saveVoiceFlowLocks = (locks) => {
  try {
    localStorage.setItem("voiceFlowLocks", JSON.stringify(locks));
  } catch (error) {
    console.error("Failed to save voiceFlowLocks to localStorage:", error);
  }
};

const loadTextFlowLocks = () => {
  try {
    const stored = localStorage.getItem("textFlowLocks");
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error("Failed to parse textFlowLocks from localStorage:", error);
    return {};
  }
};

const saveTextFlowLocks = (locks) => {
  try {
    localStorage.setItem("textFlowLocks", JSON.stringify(locks));
  } catch (error) {
    console.error("Failed to save textFlowLocks to localStorage:", error);
  }
};

const initialState = {
  // TEXT LANGUAGE DETECTION
  isTextLanguageDetecting: false,
  textLanguageDetectionData: null,
  textLanguageDetectionError: null,

  // CONTENT GENERATION
  isContentGenerating: false,
  contentGenerationData: null,
  contentGenerationError: null,

  // NER DETECTION
  isNerDetecting: false,
  nerDetectionData: null,
  nerDetectionError: null,

  // Additional states
  isInputDisabled: false,

  selectedChatId: null,
  currentUserChatId: null,
  currentBotChatId: null,

  // 🎤 Audio/Socket states (from your useState list)
  displayTranscript: "",
  transcriptType: "",
  accumulatedFinalTranscript: "",
  currentInterimTranscript: "",
  currentFinalTranscript: "",

  selectedSocketLanguageData: null,
  recordedBase64Data: null,
  socketFlags: {},
  isSocketRecording: false,
  isVoiceFlow: false,
  isSocketDataReadyToSend: false,

  // UI
  isSuggestedQuestionsVisible: true,
  isHideUserInputFields: false,

  // 🔒 VoiceFlow Locks
  voiceFlowLocks: loadVoiceFlowLocks(),
  // 🔒 TextFlow Locks
  textFlowLocks: loadTextFlowLocks(),

  isTextFlow: false,
};

const updateInputDisabled = (state) => {
  state.isInputDisabled =
    state.isTextLanguageDetecting ||
    state.isContentGenerating ||
    state.isNerDetecting;
};

const updateIsTextFlow = (state) => {
  const chatId = state.selectedChatId;
  const isLocked = chatId ? state.textFlowLocks[chatId] : false;

  // Check if user input text exists (from contentGenerationData or displayTranscript, depending on your flow)
  const hasValidTextInput = false;
  
  if (isLocked) {
    // 🔒 Locked: only allow flow if text exists
    if (hasValidTextInput) {
      state.isTextFlow = true;
    } else {
      state.isTextFlow = false;
    }
    return;
  }

  // 🔓 Unlocked: check text availability
  if (hasValidTextInput && !state.isSocketRecording) {
    state.isTextFlow = true; // ✅ Ready for text flow
  } else {
    state.isTextFlow = false; // ❌ Not ready
  }
};

const updateIsVoiceFlow = (state) => {
  const hasAudio = !!state.recordedBase64Data;

  const accuFinalTranscript =
    typeof state.accumulatedFinalTranscript === "string"
      ? state.accumulatedFinalTranscript
      : "";
  const interimTranscript =
    typeof state.currentInterimTranscript === "string"
      ? state.currentInterimTranscript
      : "";

  const hasFinalTranscript = !!accuFinalTranscript.trim();
  const hasInterim = !!interimTranscript.trim();
  const hasTranscript = hasFinalTranscript || hasInterim;

  const chatId = state.selectedChatId;
  const isLocked = chatId ? state.voiceFlowLocks[chatId] : false;

  if (isLocked) {
    // 🔒 Locked: only set ready if transcript + audio
    if (hasTranscript && hasAudio) {
      state.isVoiceFlow = true;
      state.isSocketDataReadyToSend = true;
    } else {
      state.isVoiceFlow = false;
      state.isSocketDataReadyToSend = false;
    }
    return;
  }

  // 🔓 Unlocked: recording vs stopped logic
  // CRITICAL: isSocketDataReadyToSend should ONLY be true when recording has STOPPED
  if (state.isSocketRecording) {
    // While recording: show as voice flow but NOT ready to send
    if (hasTranscript) {
      state.isVoiceFlow = true;
      state.isSocketDataReadyToSend = false; // ❌ Not ready while recording
    } else {
      state.isVoiceFlow = false;
      state.isSocketDataReadyToSend = false;
    }
  } else {
    // Recording stopped: check if we have complete data
    if (hasAudio && hasTranscript) {
      state.isVoiceFlow = true;
      state.isSocketDataReadyToSend = true; // ✅ Ready to send!
    } else {
      state.isVoiceFlow = false;
      state.isSocketDataReadyToSend = false;
    }
  }
};

const textContentSlice = createSlice({
  name: "textContent",
  initialState,
  reducers: {
    setSelectedChatId: (state, action) => {
      state.selectedChatId = action.payload;
      updateIsVoiceFlow(state);
      updateIsTextFlow(state);
    },
    setCurrentUserChatId: (state, action) => {
      state.currentUserChatId = action.payload;
    },
    setCurrentBotChatId: (state, action) => {
      state.currentBotChatId = action.payload;
    },
    // TEXT LANGUAGE DETECTION
    setIsTextLanguageDetecting: (state, action) => {
      state.isTextLanguageDetecting = action.payload;
      updateInputDisabled(state);
      updateInputDisabled(state);
    },
    setTextLanguageDetectionData: (state, action) => {
      state.textLanguageDetectionData = action.payload;
      state.textLanguageDetectionError = null;
      state.isTextLanguageDetecting = false;
      updateInputDisabled(state);
    },
    setTextLanguageDetectionError: (state, action) => {
      // Serialize error object
      const error = action.payload;
      state.textLanguageDetectionError =
        error instanceof Error
          ? { message: error.message, name: error.name, stack: error.stack }
          : error;
      state.textLanguageDetectionData = null;
      state.isTextLanguageDetecting = false;
      updateInputDisabled(state);
    },

    // CONTENT GENERATION
    setIsContentGenerating: (state, action) => {
      state.isContentGenerating = action.payload;
      updateInputDisabled(state);
    },
    setContentGenerationData: (state, action) => {
      state.contentGenerationData = action.payload;
      state.contentGenerationError = null;
      state.isContentGenerating = false;
      updateInputDisabled(state);
    },
    setContentGenerationError: (state, action) => {
      // Serialize error object
      const error = action.payload;
      state.contentGenerationError =
        error instanceof Error
          ? { message: error.message, name: error.name, stack: error.stack }
          : error;
      state.contentGenerationData = null;
      state.isContentGenerating = false;
      updateInputDisabled(state);
    },

    // NER DETECTION
    setIsNerDetecting: (state, action) => {
      state.isNerDetecting = action.payload;
      updateInputDisabled(state);
    },
    setNerDetectionData: (state, action) => {
      state.nerDetectionData = action.payload;
      state.nerDetectionError = null;
      state.isNerDetecting = false;
      updateInputDisabled(state);
    },
    setNerDetectionError: (state, action) => {
      // Serialize error object
      const error = action.payload;
      state.nerDetectionError =
        error instanceof Error
          ? { message: error.message, name: error.name, stack: error.stack }
          : error;
      state.nerDetectionData = null;
      state.isNerDetecting = false;
      updateInputDisabled(state);
    },

    // 🎤 Audio/Socket reducers
    setDisplayTranscript: (state, action) => {
      state.displayTranscript = action.payload;
    },
    setTranscriptType: (state, action) => {
      state.transcriptType = action.payload;
    },

    setCurrentInterimTranscript: (state, action) => {
      // ✅ Just replace, don't accumulate
      state.currentInterimTranscript = action.payload;
      updateIsVoiceFlow(state);
    },

    setCurrentFinalTranscript: (state, action) => {
      const transcript = action.payload?.trim();
      if (!transcript) return;

      // ✅ Accumulate to existing (like SocketComponent does with prev + response)
      state.accumulatedFinalTranscript = state.accumulatedFinalTranscript
        ? `${state.accumulatedFinalTranscript} ${transcript}`
        : transcript;

      // ✅ Clear interim when final arrives
      state.currentInterimTranscript = "";
      updateIsVoiceFlow(state);
    },

    setAccumulatedFinalTranscript: (state, action) => {
      state.accumulatedFinalTranscript = action.payload; 
      updateIsVoiceFlow(state);
    },
    setSelectedSocketLanguageData: (state, action) => {
      state.selectedSocketLanguageData = action.payload;
    },
    setRecordedBase64Data: (state, action) => {
      state.recordedBase64Data = action.payload;
      updateIsVoiceFlow(state);
    },
    setSocketFlags: (state, action) => {
      state.socketFlags = action.payload;
    },
    setIsSocketRecording: (state, action) => {
      state.isSocketRecording = action.payload;
      updateIsVoiceFlow(state);
    },
    setIsVoiceFlow: (state, action) => {
      state.isVoiceFlow = action.payload;
    },
    setIsSocketDataReadyToSend: (state, action) => {
      state.isSocketDataReadyToSend = action.payload;
    },

    // UI
    setIsSuggestedQuestionsVisible: (state, action) => {
      state.isSuggestedQuestionsVisible = action.payload;
    },

    setIsHideUserInputFields: (state, action) => {
      state.isHideUserInputFields = action.payload;
    },
    // 🔒 VoiceFlow Lock reducers
    lockVoiceFlowForChat: (state, action) => {
      const chatId = action.payload;
      if (!chatId) return;
      state.voiceFlowLocks[chatId] = true;
      saveVoiceFlowLocks(state.voiceFlowLocks);
      updateIsVoiceFlow(state);
    },
    unlockVoiceFlowForChat: (state, action) => {
      const chatId = action.payload;
      if (!chatId) return;
      state.voiceFlowLocks[chatId] = false;
      saveVoiceFlowLocks(state.voiceFlowLocks);
      updateIsVoiceFlow(state);
    },
    deleteDataForVoiceLockFlow: (state) => {
      state.voiceFlowLocks = {};
      saveVoiceFlowLocks({});
      localStorage.removeItem("voiceFlowLocks");
      updateIsVoiceFlow(state);
    },

    setIsTextFlow: (state, action) => {
      state.isTextFlow = action.payload;
    },
    lockTextFlowForChat: (state, action) => {
      const chatId = action.payload;
      if (!chatId) return;
      state.textFlowLocks[chatId] = true;
      saveTextFlowLocks(state.textFlowLocks);
    },
    unlockTextFlowForChat: (state, action) => {
      const chatId = action.payload;
      if (!chatId) return;
      state.textFlowLocks[chatId] = false;
      saveTextFlowLocks(state.textFlowLocks);
    },
    deleteDataForTextLockFlow: (state) => {
      state.textFlowLocks = {};
      saveTextFlowLocks({});
      localStorage.removeItem("textFlowLocks");
    },

    // RESET
    resetTextContentState: () => initialState,
  },
});

export const {
  setIsTextLanguageDetecting,
  setTextLanguageDetectionData,
  setTextLanguageDetectionError,
  setIsContentGenerating,
  setContentGenerationData,
  setContentGenerationError,
  resetTextContentState,
  setIsNerDetecting,
  setNerDetectionData,
  setNerDetectionError,
  setSelectedChatId,
  setCurrentUserChatId,
  setCurrentBotChatId,
  // 🎤 Audio/Socket actions
  setDisplayTranscript,
  setTranscriptType,
  setCurrentFinalTranscript,
  setAccumulatedFinalTranscript,
  setCurrentInterimTranscript,
  setSelectedSocketLanguageData,
  setRecordedBase64Data,
  setSocketFlags,
  setIsSocketRecording,
  setIsVoiceFlow,
  setIsSocketDataReadyToSend,

  // UI
  setIsSuggestedQuestionsVisible,
  setIsHideUserInputFields,

  // 🔒 VoiceFlow lock actions
  lockVoiceFlowForChat,
  unlockVoiceFlowForChat,
  deleteDataForVoiceLockFlow,

  // textFlow lock actions
  lockTextFlowForChat,
  unlockTextFlowForChat,
  deleteDataForTextLockFlow,
  setIsTextFlow
} = textContentSlice.actions;

export default textContentSlice.reducer;
